import { Subscription, debounceTime, filter, skip } from 'rxjs';
import { USE_WS_IO } from '../../../environments/environment';
import {
  FiltroEntidad,
  IOpcionesEntidad,
  ModoEntidad,
  WebSocketService,
} from './websocket.service';

/**
 * Filtro de los consumidores de servicio técnico sobre `eventogenerico`.
 *
 * Los ST son eventos genéricos con estos dos filtradores, y son un puñado de
 * documentos: el resto del room son eventos operacionales ('Evento'), decenas
 * por segundo, que estos consumidores descartan igual. Sin este filtro
 * server-side el socket los recibe todos.
 */
export const FILTRO_EVENTOS_TECNICOS = {
  filtrador: ['Servicio Técnico', 'Verificación'],
};

/** Cadencia de polling cuando el WS está caído (comportamiento histórico). */
export const INTERVALO_POLLING_SIN_WS = 30;
/** Cadencia de reconciliación cuando el WS está conectado (red de seguridad). */
export const INTERVALO_RECONCILIACION_WS = 90;

export interface OpcionesReconciliacionWs {
  /** paths de entidad a suscribir en el server (whitelist del websocket-io). */
  entidades: string[];
  /** _id del permiso activo (header x-permiso). Se evalúa al suscribir. */
  idPermiso?: () => string | undefined;
  /**
   * Callback único: re-arma el listado (fetch inmediato + timer con la
   * cadencia devuelta por intervaloActual()). Se invoca debounced ante cada
   * mensaje WS de las entidades y ante cambios de estado de conexión.
   */
  relistar: () => void;
  /** Debounce de los triggers WS, evita dobles listar() (default 1500ms). */
  debounceMs?: number;
  /**
   * Modo de entrega. Un consumidor que solo usa el mensaje como trigger de un
   * refetch debería pedir 'trigger': el server batchea y recorta el body.
   */
  modo?: ModoEntidad;
  /**
   * Filtro SERVER-SIDE (campo → valores). Sin esto el socket recibe el room
   * entero: `eventogenerico` mueve decenas de mensajes por segundo y filtrar
   * en el cliente no ahorra nada, porque el mensaje ya llegó.
   */
  filtro?: FiltroEntidad;
}

/**
 * Patrón compartido de los listados "vivos": WS como canal principal +
 * polling lento de reconciliación.
 *
 * - Suscribe las entidades por subscribe-entity (re-suscripción automática al
 *   reconectar la maneja WebSocketService).
 * - Cada update de esas entidades → relistar() debounced (NO replica el
 *   filtro de negocio del listado en el cliente: el mensaje es solo un
 *   trigger; el fetch usa la query real del servicio).
 * - Cambio de estado de conexión → relistar() para re-armar el timer con la
 *   nueva cadencia (30s caído / 90s conectado).
 *
 * Con USE_WS_IO=false es inerte: el listado sigue con su polling histórico.
 */
export class ReconciliacionWs {
  private subs: Subscription[] = [];
  private iniciada = false;
  /** Permiso con el que se suscribieron las entidades (para detectar cambios). */
  private idPermisoActual?: string;

  constructor(
    private wsService: WebSocketService,
    private opciones: OpcionesReconciliacionWs,
  ) {}

  /** Lo que se le pasa al WebSocketService por cada entidad de este consumidor. */
  private opcionesEntidad(): IOpcionesEntidad {
    return { modo: this.opciones.modo, filtro: this.opciones.filtro };
  }

  /**
   * Cadencia de polling que corresponde al estado actual del WS. Solo se
   * relaja a 90s si el server CONFIRMÓ (ack success) la suscripción de todas
   * las entidades — ej: 'alertas' se rechaza hasta que el server la soporte,
   * y ese listado debe seguir a 30s.
   */
  public intervaloActual(): number {
    const confirmadas =
      USE_WS_IO &&
      this.wsService.connected() &&
      this.opciones.entidades.every((e) =>
        this.wsService.isEntitySubscribed(e),
      );
    return confirmadas ? INTERVALO_RECONCILIACION_WS : INTERVALO_POLLING_SIN_WS;
  }

  public iniciar(): void {
    if (!USE_WS_IO) {
      return;
    }

    const idPermiso = this.opciones.idPermiso?.();

    // Los servicios globales llaman iniciar() de nuevo en reiniciar() al
    // cambiar el permiso activo. Salir temprano dejaba la suscripción del
    // server con el permiso VIEJO: el usuario seguía recibiendo updates del
    // scope anterior y no los del nuevo.
    if (this.iniciada) {
      this.actualizarPermiso(idPermiso);
      return;
    }

    this.iniciada = true;
    this.idPermisoActual = idPermiso;

    for (const entidad of this.opciones.entidades) {
      this.wsService.subscribeEntity(entidad, idPermiso, this.opcionesEntidad());
    }

    // Trigger por mensaje de entidad suscrita
    this.subs.push(
      this.wsService
        .getMessage()
        .pipe(
          filter(
            (msg) =>
              msg.motivo === 'entidad-suscrita' &&
              this.opciones.entidades.some((e) => msg.paths?.includes(e)),
          ),
          debounceTime(this.opciones.debounceMs ?? 1500),
        )
        .subscribe(() => this.opciones.relistar()),
    );

    // Cambio de conexión → re-armar timer con la nueva cadencia (y el fetch
    // inmediato del timer(0, ...) reconcilia lo perdido durante la caída).
    this.subs.push(
      this.wsService.connected$
        .pipe(skip(1))
        .subscribe(() => this.opciones.relistar()),
    );

    // Confirmación (ack) de una de nuestras entidades → re-armar con la
    // cadencia lenta (el ack llega después del evento connect).
    this.subs.push(
      this.wsService.entidadConfirmada$
        .pipe(
          filter((entidad) => this.opciones.entidades.includes(entidad)),
          debounceTime(500),
        )
        .subscribe(() => this.opciones.relistar()),
    );
  }

  /**
   * Re-suscribe las entidades con el permiso nuevo (sin tocar el refcount del
   * WebSocketService: la suscripción ya está contada por este consumidor).
   */
  private actualizarPermiso(idPermiso?: string): void {
    if (idPermiso === this.idPermisoActual) {
      return;
    }
    this.idPermisoActual = idPermiso;
    for (const entidad of this.opciones.entidades) {
      this.wsService.setEntityPermiso(entidad, idPermiso);
    }
  }

  public detener(): void {
    if (!this.iniciada) {
      return;
    }
    this.iniciada = false;
    this.idPermisoActual = undefined;
    for (const entidad of this.opciones.entidades) {
      this.wsService.unsubscribeEntity(entidad, this.opcionesEntidad());
    }
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }
}
